<div class="container">

  <header class="page-heading">
    <h1>Karnataka Stamp Duty &amp; Registration Charges Calculator</h1>
    <p>Estimate the stamp duty, cess, surcharge and registration fee payable on a property sale (conveyance/sale deed) in Karnataka, based on your sale consideration value.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">Property Sale Details</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('saleConsideration')">
        <label class="field-label" for="saleConsideration">
          Sale Consideration Value
          <span class="info-icon-wrap" tabindex="0">
            <span class="info-icon" aria-hidden="true">i</span>
            <span class="info-popover" role="tooltip">
              The price the buyer and seller actually agree to pay for the property, as written in the sale deed / agreement of sale. This is the transaction value — not the property's market value or what a bank values it at.
            </span>
          </span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="saleConsideration" type="number"
            [(ngModel)]="saleConsideration"
            [min]="C.saleValue.min" [max]="C.saleValue.max" [step]="C.saleValue.step"
            (ngModelChange)="onFieldChange('saleConsideration')"
            (blur)="onFieldBlur('saleConsideration')"
            placeholder="e.g. 50,00,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">The price agreed in the sale deed / agreement of sale.</div>
        @if (hasError('saleConsideration')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('saleConsideration') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('guidanceValue')">
        <label class="field-label" for="guidanceValue">
          Government Guidance Value
          <span class="info-icon-wrap" tabindex="0">
            <span class="info-icon" aria-hidden="true">i</span>
            <span class="info-popover" role="tooltip">
              The minimum value the Karnataka government has fixed for a property in that area for stamp duty purposes (also called the "circle rate"). Stamp duty is charged on whichever is higher — your sale price or this guidance value. Look it up on the Kaveri Online portal before you finalise a deal.
            </span>
          </span>
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="guidanceValue" type="number"
            [(ngModel)]="guidanceValue"
            [min]="C.guidanceValue.min" [max]="C.guidanceValue.max" [step]="C.guidanceValue.step"
            (ngModelChange)="onFieldChange('guidanceValue')"
            (blur)="onFieldBlur('guidanceValue')"
            placeholder="leave blank if unknown"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Stamp duty is charged on whichever is higher — sale price or guidance value. Look this up on the Kaveri Online portal before paying.</div>
        @if (hasError('guidanceValue')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('guidanceValue') }}
          </div>
        }
      </div>
    </div>

    <div class="field-group mt-sm">
      <!-- Group heading for the property-location toggle buttons below, wired via aria-labelledby (not a single-control label) -->
      <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
      <label class="field-label" id="property-location-label">Property Location</label>
      <div class="mode-toggle" role="group" aria-labelledby="property-location-label">
        <button type="button" class="mode-btn" [class.active]="areaType==='urban'"
          (click)="setAreaType('urban'); calculated && calculate()">BBMP / Corporation / Municipal Council</button>
        <button type="button" class="mode-btn" [class.active]="areaType==='rural'"
          (click)="setAreaType('rural'); calculated && calculate()">Gram Panchayat (BMRDA)</button>
      </div>
      <div class="field-hint">This decides the surcharge rate — 2% inside city/town limits, 3% inside Gram Panchayat limits.</div>
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Total Stamp Duty (incl. cess &amp; surcharge)</div>
        <div class="sc-value">{{ fmt.currency(result.totalStampDuty) }}</div>
        <div class="sc-sub">{{ (result.slab.rate * 100).toFixed(0) }}% slab rate</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Registration Fee</div>
        <div class="sc-value">{{ fmt.currency(result.registrationFee) }}</div>
        <div class="sc-sub">{{ (result.registrationRate * 100).toFixed(0) }}% of value</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Total Payable</div>
        <div class="sc-value">{{ fmt.currency(result.totalPayable) }}</div>
        <div class="sc-sub">{{ (result.effectiveRate * 100).toFixed(2) }}% of property value</div>
      </div>
    </div>

    @if (result.usedGuidanceValue) {
      <div class="card mb-md">
        <p class="field-hint" style="margin: 0;">
          Your guidance value ({{ fmt.currency(guidanceValue) }}) is higher than the sale consideration
          ({{ fmt.currency(saleConsideration) }}), so all charges below are calculated on
          <strong>{{ fmt.currency(result.dutyValue) }}</strong> — the higher of the two, as required by the Karnataka Stamp Act.
        </p>
      </div>
    }

    <div class="card charts-card mb-md">
      <div class="section-title">Charge Breakdown</div>
      <div class="chart-panel">
        <div class="chart-canvas-wrap chart-canvas-bar-solo">
          <canvas #breakdownRef></canvas>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Full Calculation</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Component</th>
              <th class="t-left">Rate</th>
              <th class="t-balance">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="t-left">Value used for duty ({{ result.slab.label }})</td>
              <td class="t-left">—</td>
              <td class="t-balance">{{ fmt.currency(result.dutyValue) }}</td>
            </tr>
            <tr>
              <td class="t-left">Stamp Duty</td>
              <td class="t-left">{{ (result.slab.rate * 100).toFixed(0) }}%</td>
              <td class="t-balance">{{ fmt.currency(result.baseStampDuty) }}</td>
            </tr>
            <tr>
              <td class="t-left">Cess (on stamp duty)</td>
              <td class="t-left">{{ (result.cessRate * 100).toFixed(0) }}%</td>
              <td class="t-balance">{{ fmt.currency(result.cessAmount) }}</td>
            </tr>
            <tr>
              <td class="t-left">Surcharge (on stamp duty) — {{ areaType === 'urban' ? 'Urban' : 'Rural' }}</td>
              <td class="t-left">{{ (result.surchargeRate * 100).toFixed(0) }}%</td>
              <td class="t-balance">{{ fmt.currency(result.surchargeAmount) }}</td>
            </tr>
            <tr>
              <td class="t-left"><strong>= Total Stamp Duty</strong></td>
              <td class="t-left"></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.totalStampDuty) }}</strong></td>
            </tr>
            <tr>
              <td class="t-left">Registration Fee</td>
              <td class="t-left">{{ (result.registrationRate * 100).toFixed(0) }}%</td>
              <td class="t-balance">{{ fmt.currency(result.registrationFee) }}</td>
            </tr>
            <tr class="prepay-highlight">
              <td class="t-left"><strong>= Total Payable</strong></td>
              <td class="t-left"><strong>{{ (result.effectiveRate * 100).toFixed(2) }}%</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.totalPayable) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card mb-md cross-link-card">
      <p>Buying with a home loan? Use our
        <a routerLink="/home-loan">Home Loan EMI Calculator</a>.</p>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is stamp duty calculated on a property sale in Karnataka?</h2>
    <p>
      Karnataka charges stamp duty as a flat slab rate on the entire sale value, not a marginal/tiered
      rate: <strong>2%</strong> up to ₹20,00,000; <strong>3%</strong> from ₹20,00,001 to ₹45,00,000;
      and <strong>5%</strong> above ₹45,00,000. On top of the stamp duty, a flat <strong>10% cess</strong>
      and a <strong>2% (urban) or 3% (rural) surcharge</strong> apply — both calculated on the stamp duty
      amount, not the property value. Registration fee is a separate, flat <strong>2%</strong> of the
      value, since the Karnataka government doubled it from 1% on 31 August 2025.
    </p>
    <ul class="emi-formula-vars">
      <li>Stamp duty is calculated on whichever is higher — the sale consideration or the government guidance value</li>
      <li>Cess (10%) and surcharge (2% urban / 3% rural) apply on top of the base stamp duty</li>
      <li>Registration fee (2%) is separate and calculated on the same value</li>
    </ul>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Is stamp duty calculated on the sale price or the guidance value?</h3>
      <p>
        Whichever is higher. If your sale consideration is lower than the government guidance value
        (circle rate) for that property, stamp duty and registration fee are both charged on the
        guidance value instead. Check the exact guidance value on the Kaveri Online portal before
        finalising a deal.
      </p>
    </div>
    <div class="faq-item">
      <h3>What's the difference between the urban and rural surcharge?</h3>
      <p>
        Properties within BBMP, City Corporation or City Municipal Council limits attract a 2%
        surcharge on the stamp duty amount. Properties within Gram Panchayat (BMRDA) limits attract a
        3% surcharge. Combined with the flat 10% cess, this makes the effective stamp duty 5.6% (urban)
        or 5.65% (rural) of the property value, at the top 5% slab.
      </p>
    </div>
    <div class="faq-item">
      <h3>Did registration charges really double in Karnataka?</h3>
      <p>
        Yes. Effective 31 August 2025, Karnataka doubled the property registration fee from 1% to 2%
        of the transaction value — the first revision since 2003 — applying to sale deeds, Joint
        Development Agreements and General Power of Attorney registrations alike.
      </p>
    </div>
    <div class="faq-item">
      <h3>Is there a stamp duty concession for women buyers in Karnataka?</h3>
      <p>
        No, not anymore. The earlier 1% stamp duty concession for women buyers was discontinued under
        the Registration (Karnataka Amendment) Act, 2024. Stamp duty and registration charges are now
        the same regardless of the buyer's gender.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/karnataka-stamp-duty-registration-charges">Karnataka stamp duty and registration charges, explained</a>
    </p>
  </div>
</section>
